import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchApplications, deleteApplication, downloadApplication, fetchApplicationById } from "../../services/api";
import Table from "../../utils/Table";
import ActionButtons from "../../utils/ActionButtons";
import { getStatusDot, formatDate } from "../../utils/TableUtils";
import ApplicationModal from "../../components/Modals/ApplicationModal";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";
import Dropdown from "react-bootstrap/Dropdown";
import downloadIcon from "../../assets/images/icons/download.svg";

function Applications() {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [filterInput, setFilterInput] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const { role } = useAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const pageSize = 10;

  const handleEdit = (applicationData) => {
    setSelectedApplication(applicationData);
    setModalShow(true);
  };

  const refreshAppData = () => {
    setModalShow(false);
    setSelectedApplication(null);
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }

  }

  const handleDelete = async (rowData) => {
    startLoading();
    try {
      const { applicantId } = rowData;
      if (!applicantId) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      const response = await deleteApplication(applicantId);

      if (response && response.message) {
        // Refresh data to get updated total count
        if (id) {
          await fetchData(id);
        } else {
          await fetchData();
        }
        showToast(response.message, "success");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An error occurred while deleting the community.", "error");
      }
    } finally {
      stopLoading();
    }
  };

  const handlDownload = async (rowData) => {
    try {
      const { applicantId } = rowData;
      if (!applicantId) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      startLoading();
      const response = await downloadApplication(applicantId);

      if (response && response.message) {
        setData((prevData) =>
          prevData.filter((applicaton) => applicaton.applicantId !== applicantId)
        );
        showToast(response.message, "success");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An error occurred while deleting the community.", "error");
      }
    } finally {
      stopLoading();
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Upload Date",
        accessor: "date",
        Cell: ({ value }) => formatDate(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.date?.toLowerCase() || "";
          const b = rowB.values.date?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Application #",
        accessor: "applicantId",
        Cell: ({ value }) => value?.toString().padStart(2, "0"),
        sortType: (rowA, rowB) => {
          const a = rowA.values.applicantId?.toString().toLowerCase() || "";
          const b = rowB.values.applicantId?.toString().toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Applicant Name",
        accessor: row => `${row.firstName} ${row.lastName}`,
        Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`,
        sortType: (rowA, rowB) => {
          const a = rowA.values.firstName?.toLowerCase() || "";
          const b = rowB.values.firstName?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Address",
        accessor: row => {
          const address = row?.location?.address || "";
          // Remove country name from address for display
          return address.replace(/,\s*(United States|United States of America|USA|U\.S\.A\.|US|U\.S\.|America)\s*$/i, "").trim();
        },
        Cell: ({ row }) => {
          const address = row.original.location?.address || "";
          // Remove country name from address for display
          return address.replace(/,\s*(United States|United States of America|USA|U\.S\.A\.|US|U\.S\.|America)\s*$/i, "").trim();
        },
        sortType: (rowA, rowB) => {
          const a = rowA.values.location?.address?.toLowerCase() || "";
          const b = rowB.values.location?.address?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => getStatusDot(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.status?.toLowerCase() || "";
          const b = rowB.values.status?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Attachments",
        accessor: "location.files",
        disableSortBy: true,
        Cell: ({ row }) => {
          const files = row.original.location?.files || [];
          return files.length > 0 ? (
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                id={`dropdown-${row.original.applicantId}`}
                className="btn-action d-flex align-items-center gap-2 border"
              >
                <img src={downloadIcon} alt="Files" className="action-icon" style={{ width: '16px' }} />
                <span className="action-label fw-bold">Files ({files.length})</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {files.map((file, index) => (
                  <Dropdown.Item
                    key={index}
                    href={file.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center gap-2"
                  >
                    <img src={downloadIcon} alt="File" style={{ width: '14px', opacity: 0.7 }} />
                    <span>File {index + 1}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <span className="text-muted">None</span>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "actions",
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <ActionButtons
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDownload={handlDownload}
            rowData={row.original}
            showDelete={role === "Admin"}
            additonalBtn={true}
          />
        ),
      },
    ];

    return baseColumns;
  }, [role]);

  const fetchData = async (id = null) => {
    setLoading(true);
    try {
      let response;
      if (id) {
        response = await fetchApplicationById(id, pageIndex + 1, pageSize);
        setData(response.data);
        // For community-specific view, set totalCount from pagination if available
        if (response.pagination) {
          setTotalCount(response.pagination.totalCount || 0);
          setPageCount(response.pagination.totalPages || 0);
        }
      } else {
        response = await fetchApplications(pageIndex + 1, pageSize, null, filterInput || null);
        setData(response.data);
        setPageCount(response.pagination.totalPages);
        setTotalCount(response.pagination.totalCount || 0);
      }
    } catch (error) {

      console.error("Error fetching data:", error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }
  }, [id, pageIndex, filterInput]);

  return (
    <div className="subMain-content">
      <div className="page-header">
        <h1 className="page-title">Applications</h1>
        {totalCount > 0 && (
          <p className="page-subtitle">
            {totalCount} {totalCount === 1 ? 'application' : 'applications'} found
          </p>
        )}
      </div>
      <Table
        columns={columns}
        data={data}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
        onPageChange={handlePageChange}
        currentPage={pageIndex}
        pageCount={pageCount}
        loading={loading}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />
      {selectedApplication && <ApplicationModal
        show={modalShow}
        data={selectedApplication}
        onHide={refreshAppData}
      />}
    </div>
  );
}

export default Applications;
